import { useState } from 'react';
import BillingoService from '../../Services/BillingoService';
import { apiErrorText, DOC_TYPES } from '../../Utils/billingoDocuments';

// A link to the PDF uploaded onto the document's booking, signed just now by
// the API (the PDF is private; its links last minutes).
async function signedUploadLink(doc) {
  const docs = await BillingoService.listDocumentsForBooking({ _id: doc.booking_ids?.[0] });
  const url = docs.find((d) => d.type === DOC_TYPES.LEGACY)?.pdf_url;
  if (!url) throw new Error('No uploaded PDF was found for this booking.');
  return url;
}

// A Billingo document's PDF, fetched through the backend proxy, as a blob URL.
async function billingoPdfUrl(doc) {
  const pdf = await BillingoService.getDocumentPdf(doc.billingo_document_id);
  if (!pdf?.content_base64) throw new Error('Billingo returned no PDF for this document.');
  const byteChars = atob(pdf.content_base64);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i += 1) bytes[i] = byteChars.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: pdf.content_type || 'application/pdf' }));
}

// Open an invoice document's PDF in a new tab. An uploaded document listed
// with its signed http(s) link opens straight away; one without a link in
// hand (the booking's Details card knows only the booking) gets a fresh one
// from the API. Billingo documents have no link (the PDF needs the API key),
// so the bytes come through the authenticated backend proxy as a blob.
//
// The tab is opened FIRST, synchronously inside the click: a window opened
// after an await is no longer a user gesture and pop-up blockers eat it,
// which made "Open PDF" do nothing at all. It shows a loading line until the
// PDF is in. Throws (after closing the tab) when the PDF can't be had, so the
// caller can say why.
export default async function openInvoicePdf(doc) {
  if (!doc) return;
  if (doc.pdf_url && /^https?:\/\//i.test(doc.pdf_url)) {
    window.open(doc.pdf_url, '_blank', 'noopener');
    return;
  }
  const uploaded = doc.type === DOC_TYPES.LEGACY;
  if (!uploaded && !doc.billingo_document_id) return;

  const tab = window.open('', '_blank');
  if (!tab) throw new Error('The browser blocked the PDF tab — allow pop-ups for this site and try again.');
  tab.opener = null;
  tab.document.title = `${doc.number || 'Invoice'} — loading…`;
  tab.document.body.textContent = uploaded ? 'Loading the PDF…' : 'Loading the PDF from Billingo…';

  try {
    tab.location.replace(uploaded ? await signedUploadLink(doc) : await billingoPdfUrl(doc));
  } catch (err) {
    tab.close();
    throw err;
  }
}

/**
 * openInvoicePdf for a button: `open(doc)` never throws — `error` says why the
 * PDF didn't open — and `opening` is the id of the document being fetched,
 * so its button can show it is working.
 */
export function useInvoicePdf() {
  const [opening, setOpening] = useState(null);
  const [error, setError] = useState('');

  const open = async (doc) => {
    setError('');
    setOpening(doc.id);
    try {
      await openInvoicePdf(doc);
    } catch (err) {
      setError(`Couldn't open the PDF of ${doc.number || 'this document'}: ${apiErrorText(err, 'the download failed.')}`);
    } finally {
      setOpening(null);
    }
  };

  return {
    open, opening, error, setError,
  };
}
